<template>
  <div class="brand-analytics-page">
    <section class="hero">
      <div class="container hero-grid">
        <div class="hero-copy">
          <p class="eyebrow">Brand Analytics</p>
          <h1>{{ texts.title }}</h1>
          <p class="lead">{{ texts.lead }}</p>
          <div class="hero-actions">
            <a class="action-link primary" :href="contactHref">{{ texts.contactLink }}</a>
            <router-link :to="{ name: 'spApiComplianceDetail' }" class="action-link">
              {{ texts.complianceLink }}
            </router-link>
          </div>
        </div>
        <div class="hero-card">
          <h2>{{ texts.audienceTitle }}</h2>
          <ul>
            <li v-for="item in texts.audienceItems" :key="item">{{ item }}</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="content">
      <div class="container stack">
        <section class="card">
          <h2>{{ texts.featureTitle }}</h2>
          <div class="feature-grid">
            <article v-for="item in texts.featureItems" :key="item.title" class="feature-item">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </section>

        <section class="card">
          <h2>{{ texts.reportTitle }}</h2>
          <p>{{ texts.reportLead }}</p>
          <ul class="report-list">
            <li v-for="item in texts.reportItems" :key="item">{{ item }}</li>
          </ul>
          <p class="note">{{ texts.reportNote }}</p>
        </section>

        <section class="card">
          <h2>{{ texts.boundaryTitle }}</h2>
          <div class="boundary-grid">
            <div v-for="item in texts.boundaryItems" :key="item.title" class="boundary-item">
              <strong>{{ item.title }}</strong>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </section>

        <section class="card">
          <h2>{{ texts.usageTitle }}</h2>
          <ul>
            <li v-for="item in texts.usageItems" :key="item">{{ item }}</li>
          </ul>
        </section>
      </div>
    </section>
  </div>
</template>

<script>
import { localeState } from "@/utils/locale";

export default {
  name: "BrandAnalytics",
  data() {
    return {
      copy: {
        zh: {
          title: "亚马逊品牌分析功能说明",
          lead:
            "深圳市万俐电子商务有限公司为已授权的亚马逊品牌卖家和品牌所有者提供品牌经营分析工具，用于查看品牌搜索表现、复购表现、市场篮子关系和经营趋势，辅助品牌团队做广告优化、选品决策和内容运营。",
          contactLink: "联系我们",
          complianceLink: "查看 SP-API 合规说明",
          contactSubject: "Brand Analytics 功能咨询",
          audienceTitle: "适用对象",
          audienceItems: [
            "已完成品牌备案并具备相应数据权限的亚马逊卖家",
            "品牌所有者及其授权运营团队",
            "经客户明确授权后代为处理数据的服务团队",
          ],
          featureTitle: "我们提供的品牌分析功能",
          featureItems: [
            {
              title: "搜索词与搜索目录表现",
              desc:
                "帮助品牌团队查看品牌商品在搜索场景下的曝光、点击、转化和目录表现，支持分析核心关键词、核心 ASIN 和流量变化趋势。",
            },
            {
              title: "市场篮子与替代购买分析",
              desc:
                "用于识别消费者联购关系、替代购买关系和关联商品机会，帮助品牌进行组合销售、关联搭配和竞品策略分析。",
            },
            {
              title: "复购与客户粘性分析",
              desc:
                "用于观察重复购买情况、回购节奏和品牌客户留存趋势，为会员运营、复购活动和生命周期营销提供支持。",
            },
            {
              title: "品牌经营报表与内部看板",
              desc:
                "将品牌分析数据整合进内部经营看板，用于品牌经理、运营和广告团队的日常复盘与决策，不面向公众提供开放数据查询服务。",
            },
          ],
          reportTitle: "与 Brand Analytics 角色对应的数据使用场景",
          reportLead:
            "在客户具备相应资格并完成明确授权后，我们可能基于 Amazon SP-API 的 Brand Analytics 相关数据类型，为客户提供以下分析能力：",
          reportItems: [
            "`GET_BRAND_ANALYTICS_SEARCH_QUERY_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_SEARCH_CATALOG_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_MARKET_BASKET_REPORT`",
            "`GET_BRAND_ANALYTICS_REPEAT_PURCHASE_REPORT`",
          ],
          reportNote: "实际使用范围以 Amazon 对客户资格、应用角色和授权状态的要求为准。",
          boundaryTitle: "授权方式与数据边界",
          boundaryItems: [
            {
              title: "明确授权后访问",
              desc:
                "仅在客户完成授权后访问其品牌分析数据，不会在未授权状态下采集或查看相关信息。",
            },
            {
              title: "仅服务授权客户",
              desc:
                "数据仅用于该客户自身品牌运营和内部管理，不会用于对外分发的公共数据产品。",
            },
            {
              title: "不出售不转让",
              desc:
                "不会出售、出租、转让、许可或公开披露通过 Amazon SP-API 获取的品牌分析数据。",
            },
            {
              title: "不做跨客户公开聚合",
              desc:
                "不会将多个卖家或多个品牌的数据汇总后向公众展示、售卖或提供开放下载。",
            },
          ],
          usageTitle: "典型业务用途",
          usageItems: [
            "品牌关键词投放和自然流量趋势分析",
            "核心 ASIN 的点击、转化和目录表现监控",
            "复购商品识别与会员营销策略优化",
            "关联购买关系分析与套装搭配策略制定",
            "品牌周报、月报和经营复盘看板生成",
          ],
        },
        en: {
          title: "Amazon Brand Analytics Feature Overview",
          lead:
            "Hunan Panxian Intelligent Technology Co., Ltd. provides brand performance analytics tools for authorized Amazon brand sellers and brand owners to review search performance, repeat purchase behavior, market basket relationships, and business trends, helping brand teams optimize advertising, product selection, and content operations.",
          contactLink: "Contact Us",
          complianceLink: "View SP-API Compliance Statement",
          contactSubject: "Brand Analytics feature inquiry",
          audienceTitle: "Intended Users",
          audienceItems: [
            "Amazon sellers who have completed brand registry and hold the required data permissions",
            "Brand owners and their authorized operations teams",
            "Service teams processing data on behalf of customers after explicit authorization",
          ],
          featureTitle: "Brand Analytics Features We Provide",
          featureItems: [
            {
              title: "Search Query and Search Catalog Performance",
              desc:
                "Help brand teams review impressions, clicks, conversions, and catalog performance in Amazon search scenarios, and analyze core keywords, core ASINs, and traffic trends.",
            },
            {
              title: "Market Basket and Substitute Purchase Analysis",
              desc:
                "Identify co-purchase relationships, substitute purchase behavior, and related product opportunities to support bundling, cross-selling, and competitor strategy analysis.",
            },
            {
              title: "Repeat Purchase and Customer Retention Analysis",
              desc:
                "Track repeat purchase behavior, repurchase cycles, and customer retention trends to support membership operations, repurchase campaigns, and lifecycle marketing.",
            },
            {
              title: "Brand Reporting and Internal Dashboards",
              desc:
                "Integrate brand analytics data into internal dashboards for brand managers, operations teams, and advertising teams for routine reviews and decision-making, without offering public data query services.",
            },
          ],
          reportTitle: "Data Usage Scenarios Aligned with the Brand Analytics Role",
          reportLead:
            "After customers meet the required eligibility and provide explicit authorization, we may use the following Amazon SP-API Brand Analytics data types to deliver analytics capabilities:",
          reportItems: [
            "`GET_BRAND_ANALYTICS_SEARCH_QUERY_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_SEARCH_CATALOG_PERFORMANCE_REPORT`",
            "`GET_BRAND_ANALYTICS_MARKET_BASKET_REPORT`",
            "`GET_BRAND_ANALYTICS_REPEAT_PURCHASE_REPORT`",
          ],
          reportNote:
            "Actual usage remains subject to Amazon requirements regarding customer eligibility, application role, and authorization status.",
          boundaryTitle: "Authorization Method and Data Boundaries",
          boundaryItems: [
            {
              title: "Access Only After Explicit Authorization",
              desc:
                "We access brand analytics data only after customer authorization and do not collect or view such information without authorization.",
            },
            {
              title: "Only for the Authorized Customer",
              desc:
                "Data is used only for the customer's own brand operations and internal management, not for public-facing data products.",
            },
            {
              title: "No Sale or Transfer",
              desc:
                "We do not sell, rent, transfer, license, or publicly disclose brand analytics data obtained through Amazon SP-API.",
            },
            {
              title: "No Public Cross-Customer Aggregation",
              desc:
                "We do not aggregate data from multiple sellers or brands for public display, sale, or open download.",
            },
          ],
          usageTitle: "Typical Business Uses",
          usageItems: [
            "Brand keyword advertising and organic traffic trend analysis",
            "Monitoring clicks, conversions, and catalog performance for core ASINs",
            "Identifying repeat-purchase products and improving membership marketing strategies",
            "Analyzing related purchases and defining bundle strategies",
            "Generating weekly, monthly, and business review dashboards for brand teams",
          ],
        },
      },
    };
  },
  computed: {
    locale() {
      return localeState.locale;
    },
    texts() {
      return this.copy[this.locale] || this.copy.zh;
    },
    contactHref() {
      return `mailto:Lin@whanley.com?subject=${encodeURIComponent(
        this.texts.contactSubject
      )}`;
    },
  },
};
</script>

<style scoped>
.brand-analytics-page {
  background: #f6f9ff;
  min-height: 100%;
  padding-bottom: 4rem;
}

.container {
  margin: 0 auto;
  max-width: 80rem;
}

.hero {
  background: linear-gradient(140deg, #10214f, #1d4ed8 58%, #6da8ff);
  color: #fff;
  padding: 4rem 1.25rem 3rem;
}

.hero-grid {
  display: grid;
  gap: 1.25rem;
}

.eyebrow {
  font-size: 0.875rem;
  letter-spacing: 0.14em;
  margin-bottom: 0.75rem;
  opacity: 0.84;
  text-transform: uppercase;
}

h1 {
  font-size: 2.2rem;
  line-height: 1.22;
  margin: 0;
}

.lead {
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.9;
  margin-top: 1rem;
  max-width: 48rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-top: 1.5rem;
}

.action-link {
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 999px;
  color: #fff;
  padding: 0.8rem 1.2rem;
  text-decoration: none;
}

.action-link.primary {
  background: #fff;
  color: #173169;
}

.hero-card,
.card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 14px 40px rgba(28, 65, 148, 0.1);
}

.hero-card {
  color: #173169;
  padding: 1.5rem;
}

.hero-card h2,
.card h2 {
  color: #173169;
  margin: 0 0 1rem;
  text-align: left;
}

.hero-card ul,
.card ul {
  margin: 0;
  padding-left: 1.25rem;
}

.hero-card li,
.card li,
.card p {
  color: #344054;
  line-height: 1.85;
  text-align: left;
}

.content {
  padding: 0 1.25rem;
}

.stack {
  display: grid;
  gap: 1.25rem;
  margin-top: -1.5rem;
}

.card {
  padding: 1.5rem;
}

.feature-grid,
.boundary-grid {
  display: grid;
  gap: 1rem;
}

.feature-item,
.boundary-item {
  background: #f7faff;
  border: 1px solid #d8e5ff;
  border-radius: 0.875rem;
  padding: 1rem;
}

.feature-item h3,
.boundary-item strong {
  color: #173169;
}

.feature-item h3 {
  font-size: 1.05rem;
  margin: 0 0 0.75rem;
  text-align: left;
}

.feature-item p,
.boundary-item p {
  margin: 0;
}

.report-list {
  background: #0f172a;
  border-radius: 0.875rem;
  margin-top: 1rem;
  padding: 1rem 1.25rem 1rem 2rem;
}

.report-list li {
  color: #dbeafe;
  font-family: Menlo, Monaco, Consolas, monospace;
}

.note {
  color: #52607a;
  margin-top: 1rem;
}

@media (min-width: 1024px) {
  .hero {
    padding: 5rem 2rem 3.5rem;
  }

  .hero-grid {
    align-items: start;
    grid-template-columns: minmax(0, 1.6fr) minmax(300px, 0.9fr);
  }

  .stack {
    gap: 1.5rem;
    margin-top: -2rem;
  }

  .card {
    padding: 2rem;
  }

  .feature-grid,
  .boundary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
